import { Card, Form, Input, Button, Space, Message, Checkbox } from "@arco-design/web-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AccountAPI } from "@/apis/accounting";
import { ItemAPI } from "@/apis/material";
import { InputNumber, InputCurrency } from "@/components/Numeric";
import { EnumSelect } from "@/components/Enum";
import AccountSelect from "@/components/AccountSelect";
import UOMSelect from "@/components/UOMSelect";
import VendorSelect from "@/components/VendorSelect";
import Decimal from "decimal.js";
import { ItemTypeDef, ItemType, ItemSafetyStockType } from "@/enums/material";
import { AccountType, AccountCategory } from "@/enums/accounting";

const FormItem = Form.Item;
const TextArea = Input.TextArea;

export const ItemAdd = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [uom, setUOM] = useState();
    const [itemType, setItemType] = useState();
    const [cogsAccountLabel, setCogsAccountLabel] = useState();
    const [safetyStockLevelDaysEnabled, setSafetyStockLevelDaysEnabled] = useState(true);
    const [safetyStockLevelUomEnabled, setSafetyStockLevelUomEnabled] = useState(false);

    useEffect(() => {
        const init = async () => {
            const cogsAccount = await AccountAPI.getByName("Cost of Goods Sold");
            const incomeAccount = await AccountAPI.getByName("Revenue");
            const inventoryAccount = await AccountAPI.getByName("Inventory");
            form.setFieldsValue({
                itemType: ItemType.Inventory,
                cogsAccountId: cogsAccount.id,
                incomeAccountId: incomeAccount.id,
                inventoryAccountId: inventoryAccount.id,
                safetyStockLevel: 60,
            });
            onItemTypeChange(ItemType.Inventory);
        };
        init();
    }, [form]);

    const onUOMChange = (val) => {
        setUOM(val);
    };

    const onItemTypeChange = (value) => {
        setItemType(value);
        setCogsAccountLabel(value === ItemType.NonInventory ? "COGS/Expense Account" : "COGS Account");
    };

    const onSalePriceChange = (val) => {
        const cost = form.getFieldValue("stdCost");
        const margin = calcMargin(cost, val);
        if (margin == null) return;
        form.setFieldsValue({ defaultMargin: margin, defaultMarginPercent: margin * 100 });
    };

    const onMarginChange = (val) => {
        const cost = form.getFieldValue("stdCost");
        if (cost == null || val == null || val === 1) return;
        const price = new Decimal(cost / (1 - val)).toDecimalPlaces(5).toNumber();
        form.setFieldsValue({ salePrice: price });
    };

    const calcMargin = (cost, price) => {
        if (cost == null || price == null || price === 0) return null;
        return new Decimal((price - cost) / price).toDecimalPlaces(5).toNumber();
    };

    const onSubmit = async () => {
        try {
            const values = await form.validate();
            if (safetyStockLevelDaysEnabled) {
                values.safetyStockType = ItemSafetyStockType.Days;
            } else if (safetyStockLevelUomEnabled) {
                values.safetyStockType = ItemSafetyStockType.UOM;
            } else {
                values.safetyStockType = ItemSafetyStockType.None;
            }
            setLoading(true);
            const id = await ItemAPI.add(values);
            Message.success("Item created successfully");
            navigate(`/workspace/material/items/${id}`);
        } catch (error) {
            if (error.name !== 'ValidationError') {
                Message.error(error.message || "Failed to create item");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card title="Add New Item" extra={
            <Space>
                <Button type="primary" onClick={onSubmit} loading={loading}>Submit</Button>
                <Button onClick={() => navigate("/workspace/material/items")}>Cancel</Button>
            </Space>
        }>
            <Form form={form} colon
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 15 }}
            >
                <FormItem field="itemType" label="Item Type" required rules={[{ required: true }]}>
                    <EnumSelect enumObj={ItemTypeDef} onChange={onItemTypeChange} />
                </FormItem>
                <FormItem field="itemNo" label="Item Number" required rules={[{ required: true }]}>
                    <Input />
                </FormItem>
                <FormItem field="itemName" label="Item Name" required rules={[{ required: true }]}>
                    <Input />
                </FormItem>
                <FormItem field="description" label="Description">
                    <TextArea />
                </FormItem>
                <FormItem field="stdCost" label="Standard Cost">
                    <InputCurrency onChange={val => {
                        const margin = form.getFieldValue("defaultMargin");
                        const price = form.getFieldValue("salePrice");
                        if (margin != null) {
                            onMarginChange(margin);
                        } else if (price != null) {
                            onSalePriceChange(price);
                        }
                    }} />
                </FormItem>
                <FormItem field="uom" label="Unit of Measure(UOM)" required rules={[{ required: true }]}>
                    <UOMSelect onChange={onUOMChange} />
                </FormItem>
                <FormItem label="Additional UOM">
                    <Space>
                        <FormItem field="additionalUom" style={{ width: 120 }}>
                            <UOMSelect disabledList={[uom]} />
                        </FormItem>
                        <FormItem>=</FormItem>
                        <FormItem field="additionalUomThreshold">
                            <InputNumber suffix={uom} />
                        </FormItem>
                    </Space>
                </FormItem>
                <FormItem field="defaultMarginPercent" label="Default Margin">
                    <InputNumber precision={3} onChange={val => {
                        const margin = val / 100;
                        form.setFieldValue("defaultMargin", margin);
                        onMarginChange(margin);
                    }} suffix="%" />
                </FormItem>
                <FormItem field="defaultMargin" hidden>
                    <Input />
                </FormItem>
                <FormItem field="salePrice" label="Sales Price">
                    <InputCurrency onChange={val => onSalePriceChange(val)} />
                </FormItem>
                <FormItem field="cogsAccountId" label={cogsAccountLabel} required rules={[{ required: true }]}>
                    {itemType === ItemType.NonInventory ?
                        <AccountSelect category={AccountCategory.Expenses} />
                        :
                        <AccountSelect type={AccountType.COGS} />
                    }
                </FormItem>
                <FormItem field="incomeAccountId" label="Income Account" required rules={[{ required: true }]}>
                    <AccountSelect type={AccountType.Income} />
                </FormItem>
                {itemType === ItemType.Inventory &&
                    <FormItem field="inventoryAccountId" label="Inventory Account" required rules={[{ required: true }]}>
                        <AccountSelect type={AccountType.Inventory} />
                    </FormItem>
                }
                <FormItem label="Safety Stock Level">
                    <Space direction="vertical">
                        <Space>
                            <FormItem>
                                <Checkbox checked={safetyStockLevelUomEnabled}
                                    onChange={(checked) => {
                                        setSafetyStockLevelUomEnabled(checked);
                                        setSafetyStockLevelDaysEnabled(!checked);
                                    }} />
                            </FormItem>
                            <FormItem field="safetyStockLevelUom">
                                <InputNumber suffix={uom} style={{ width: 200 }} />
                            </FormItem>
                        </Space>
                        <Space>
                            <FormItem>
                                <Checkbox checked={safetyStockLevelDaysEnabled}
                                    onChange={(checked) => {
                                        setSafetyStockLevelUomEnabled(!checked);
                                        setSafetyStockLevelDaysEnabled(checked);
                                    }} />
                            </FormItem>
                            <FormItem field="safetyStockLevel">
                                <InputNumber suffix="days" style={{ width: 200 }} />
                            </FormItem>
                        </Space>
                    </Space>
                </FormItem>
                <FormItem label="Preferred Vendor" style={{ marginBottom: 0 }}>
                    <Space>
                        <FormItem field="preferredVendorId">
                            <VendorSelect />
                        </FormItem>
                        <FormItem field="preferredVendorLeadTime" labelAlign="left" label="Lead Time(days)">
                            <Input />
                        </FormItem>
                    </Space>
                </FormItem>
            </Form>
        </Card>
    );
};
